import React from 'react';
import {
  Avatar,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ShieldIcon from '@mui/icons-material/Shield';
import WorkIcon from '@mui/icons-material/Work';
import { useTranslation } from 'react-i18next';
import { NS } from '../../../i18n/constants/keys';
import type { IProfileSection } from '../interfaces';

interface IProfileSidebarProps {
  sections: IProfileSection[];
  activeSection: string;
  onSelect: (sectionId: string) => void;
}

const SECTION_ICONS: Record<string, typeof PersonIcon> = {
  personal: PersonIcon,
  account: SettingsIcon,
  notifications: NotificationsIcon,
  privacy: ShieldIcon,
  candidate: WorkIcon,
};

/**
 * Responsive section navigation. Mobile-first: horizontally scrollable pill
 * `Tabs` (brand theme styles them like `/mui-preview`) on xs–sm viewports;
 * vertical icon `List` on md and up. Minimum 44px touch targets on mobile.
 */
export const ProfileSidebar = ({ sections, activeSection, onSelect }: IProfileSidebarProps) => {
  const { t } = useTranslation(NS.profile);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  if (!isDesktop) {
    return (
      <Paper sx={{ px: 1, py: 0.5, mb: 0 }}>
        <Tabs
          value={activeSection}
          onChange={(_e, value: string) => onSelect(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Profile sections"
        >
          {sections.map((section) => {
            const Icon = SECTION_ICONS[section.id] ?? PersonIcon;
            return (
              <Tab
                key={section.id}
                value={section.id}
                label={t(section.titleKey)}
                icon={<Icon fontSize="small" />}
                iconPosition="start"
                sx={{ minHeight: 44 }}
              />
            );
          })}
        </Tabs>
      </Paper>
    );
  }

  return (
    <Paper component="nav" aria-label="Profile sections" sx={{ p: 1 }}>
      <List disablePadding>
        {sections.map((section) => {
          const Icon = SECTION_ICONS[section.id] ?? PersonIcon;
          const isActive = activeSection === section.id;
          return (
            <ListItemButton
              key={section.id}
              selected={isActive}
              onClick={() => onSelect(section.id)}
              aria-current={isActive ? 'page' : undefined}
              sx={{ borderRadius: 2, mb: 0.5, alignItems: 'flex-start' }}
            >
              <ListItemAvatar sx={{ minWidth: 44 }}>
                <Avatar
                  variant="rounded"
                  sx={
                    isActive
                      ? { bgcolor: 'primary.main', color: 'primary.contrastText', width: 36, height: 36 }
                      : { bgcolor: 'grey.100', color: 'text.secondary', width: 36, height: 36 }
                  }
                >
                  <Icon fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={t(section.titleKey)}
                secondary={t(section.descriptionKey)}
                primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 700 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Paper>
  );
};
